import {Select, SelectProps} from 'antd';
import {TreeNodeNormal} from 'antd/es/tree/Tree';
import {
    AccountBean,
    AdGroupBean,
    AdGroupListItemBean,
    CampaignBean,
    CreativeBean,
    ItemResponseAccountBeanObject,
    TransactionBean
} from 'common/api/api';
import {accountApi} from 'common/api/entitiesApi';
import {AppContextType} from 'common/context/AppContext';
import {StorageKey} from 'common/context/Storage';
import {User} from 'common/context/UserContext';
import Moment from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import RoleEnum = AccountBean.RoleEnum
import AdFraudProtectionTypeEnum = AdGroupBean.AdFraudProtectionTypeEnum
import VisibilityTypeEnum = AdGroupBean.VisibilityTypeEnum

dayjs.extend(utc);
dayjs.extend(timezone);

export const SESSION_IS_EXPIRED = 'Your login session has expired.  Please log in again.'
export const ACCESS_IS_DENIED = 'Access is denied'

export function toTimezone(date: dayjs.Dayjs, timezoneId: string | undefined): dayjs.Dayjs {
    return date.tz(timezoneId);
}

export function isAuthenticated() {
    if (localStorage.getItem(StorageKey.Authenticated) === 'true') {
        let expirationString = localStorage.getItem(StorageKey.TokenExpiration)
        if (expirationString) {
            let expiration: number = +expirationString
            if (expiration > Date.now()) {
                return true
            }
        }
    }
    return false
}

export function isAdmin(user) {
    return user?.role == RoleEnum.Admin
}

export async function setLoggedUserProps(setUser: (u: User) => void) {
    await accountApi.accountProps().then((item: ItemResponseAccountBeanObject) => {
        if (item.data) {
            setUser({
                id: item.data.id ?? -1,
                name: item.data.name ?? '',
                email: item.data.email ?? '',
                role: item.data.role ?? undefined,
                advertiserId: item.data.advertiserId ?? undefined,
                agencyId: item.data.agencyId ?? undefined,
                agencyPublicId: item.data.agencyPublicId,
                timezoneId: item.data.timezoneId || 'UTC',
                active: item.data.active || false
            })
        }
    })
}

export const LIST_PAGE_SIZE = 10

export function getCopyName(name: string): string
export function getCopyName(name?: string | null): string | undefined
export function getCopyName(name?: string | null) {
    return name ? `${name} (Copy)` : undefined
}

export function uuidV4() {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: any) =>
        // tslint:disable-next-line:no-bitwise
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    )
}

export function downloadCSVData(fileName: string, csvData: Blob) {
    if (window.navigator['msSaveOrOpenBlob'] != undefined) {  // IE hack; see http://msdn.microsoft.com/en-us/library/ie/hh779016.aspx
        window.navigator['msSaveBlob'](csvData, 'filename.csv');
    } else {
        const a = window.document.createElement('a');
        a.href = URL.createObjectURL(csvData);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();  // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
        document.body.removeChild(a);
    }
}

export function getTreeData(list: { name: string }[]) {
    let namesList: string[] = [];
    list.forEach(value => namesList.push(value.name));
    let root: TreeNodeNormal[] = [];

    namesList.forEach((item, index) => {
            let groupName = item.split(' ')[0];
            let group = root.find(node => (node.key as string).localeCompare(groupName + '_group') == 0);

            if (group != undefined && group.children != undefined) {
                group.children.push({
                    title: item,
                    key: item
                });
            } else {
                let itemNext = namesList[index + 1];

                if (itemNext != null) {
                    let groupNameNext = namesList[index + 1].split(' ')[0];

                    if (groupName.localeCompare(groupNameNext) == 0) {
                        let newGroup: TreeNodeNormal = {
                            title: groupName,
                            key: groupName + '_group',
                            children: [{
                                title: item,
                                key: item
                            }]
                        };
                        root.push(newGroup);
                    } else {
                        root.push({
                            title: item,
                            key: item,
                            children: []
                        })
                    }
                }
            }
        }
    );
    return root;
}

export const groupSspsByFormats = (sspList, autoUpdate) => {
    const sortedSspList = sspList.sort((a, b) => a.name.localeCompare(b.name));
    const grouped = sortedSspList.reduce((acc, ssp) => {
        const formats: CreativeBean.FormatEnum[] = ssp.formats?.length > 0 ? ssp.formats : ['Other'];
        formats.forEach(format => {
            const prettyFormat = formatNames[format] || format;
            if (!acc[prettyFormat]) {
                acc[prettyFormat] = [];
            }
            acc[prettyFormat].push({
                title: ssp.name,
                value: ssp.id + '-' + format,
                key: ssp.id + '-' + format
            });
        })
        return acc;
    }, {});

    let root: any = [];

    Object.keys(grouped).forEach((format) => (
        root.push({
                title: format,
                value: format,
                key: format,
                children: grouped[format]
            }
        )));

    root = root.sort((a, b) => a.title != 'Other' && a.title.localeCompare(b.title))

    root.unshift({
        checkable: false,
        title: autoUpdate ? 'Select All (Auto Update: On)' : 'Select All (Auto Update: Off)',
        key: -1,
        value: -1,
        children: []
    });

    return root;
};

export function sorterFunc(getData: Function) {
    return (a, b) => {
        let dataA: any = getData(a) != null ? getData(a) : '';
        let dataB: any = getData(b) != null ? getData(b) : '';

        if (typeof dataA === 'number' && typeof dataB === 'number') {
            return dataA - dataB;
        } else {
            return dataA.localeCompare(dataB)
        }
    }
}

export function getFilterData(arr: {}[], getData: Function) {
    let filterData: { text: string, value: string }[] = [];
    arr.forEach((value: any) => filterData.push({
        text: getData(value),
        value: getData(value)
    }));
    return filterData.filter((v, i, a) => a.findIndex(t => v.text === t.text && v.value === t.value) === i);
}

export function filterFunc(getData: Function) {
    return (value, record) => {
        return getData(record).toString().localeCompare(value) === 0
    }
}

export function clone(obj) {
    var copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' != typeof obj) {
        return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (var i = 0, len = obj.length; i < len; i++) {
            copy[i] = clone(obj[i]);
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        for (var attr in obj) {
            if (obj.hasOwnProperty(attr)) {
                copy[attr] = clone(obj[attr]);
            }
        }
        return copy;
    }

    throw new Error('Unable to copy obj! Its type isn\'t supported.');
}

export function normalizeArrayFields(fieldNames: string[], form) {
    fieldNames.forEach(name => {
        if (form.isFieldTouched(name)) {
            const value = form.getFieldValue(name)
            typeof value === 'string' && form.setFieldsValue({
                [name]: value.replace(/\s/g, '').replace(/,*$/, '').split(',')
            });
        }
    })
}

export function getParentId(childId: number, childList: any, parentField: Function) {
    let finder = entity => entity.id === childId;
    let childItem = childList.find(item => finder(item));
    return parentField(childItem);
}

export function calcDaysLeft(bean: AdGroupListItemBean | CampaignBean): any {
    let startDate = Moment(bean.startDate);
    let now = Moment();
    if (!bean.endDate) {
        return now > startDate ? 0 : startDate.diff(now, 'days');
    }
    let endDate = Moment(bean.endDate);

    if (now > startDate && now < endDate) {
        return endDate.diff(now, 'days');
    } else if (now < startDate) {
        return endDate.diff(startDate, 'days');
    }
    return 0;
}

export function calcLeftPeriod(bean: AdGroupListItemBean | CampaignBean, timezoneId: any): any {
    let startDate = toTimezone(dayjs(bean.startDate), timezoneId);
    let endDate = toTimezone(dayjs(bean.endDate), timezoneId);
    let now = toTimezone(dayjs(), timezoneId);

    if (now > startDate && now < endDate) {
        let remainingDays = endDate.diff(now, 'days');
        if (remainingDays < 1) {
            let endOfDay = now.endOf('day');
            let remainingHours = endOfDay.diff(now, 'hours');
            remainingHours = remainingHours === 0 ? 1 : remainingHours;
            return remainingHours > 1 ? `${remainingHours} Hours Left` : `${remainingHours} Hour Left`;
        }
        return remainingDays > 1 ? `${remainingDays} Days Left` : `${remainingDays} Day Left`;
    } else if (now < startDate) {
        const remainingDays = startDate.diff(now, 'days');
        if (remainingDays < 1) {
            let endOfDay = now.endOf('day');
            let remainingHours = endOfDay.diff(now, 'hours');
            return `Starts in ${remainingHours} Hours`;
        }
        return `Starts in ${remainingDays} Days`;
    }
    return 'Completed';
}

export function useActionsMediaQuery(breakpoint: string) {
    const [areActionsVertical, setAreActionsVertical] = useState<boolean>(false)

    const handleActionsMediaQuery = useCallback((event) => {
        const isScreenNarrow = event.matches
        setAreActionsVertical(isScreenNarrow)
    }, [])

    useEffect(() => {
        const actionsMediaQuery = window.matchMedia(`(max-width: ${breakpoint})`)
        if (actionsMediaQuery.matches) {
            setAreActionsVertical(true)
        }
        actionsMediaQuery.addListener(handleActionsMediaQuery)
        return () => {
            actionsMediaQuery.removeListener(handleActionsMediaQuery)
        }
    }, [])

    return areActionsVertical
}

export const withActionsMediaQuery = (breakpoint: string) => (WrappedComponent: React.ComponentType<any>) => {
  return class extends React.Component<any> {
    state = {
      matches: false,
    }

    private mediaQuery!: MediaQueryList

    componentDidMount() {
      this.mediaQuery = window.matchMedia(breakpoint)
      this.setState({ matches: this.mediaQuery.matches })
      this.mediaQuery.addListener(this.handleActionsMediaQuery)
    }

    componentWillUnmount() {
      this.mediaQuery.removeListener(this.handleActionsMediaQuery)
    }

    handleActionsMediaQuery = (event: MediaQueryListEvent) => {
      this.setState({ matches: event.matches })
    }

    render() {
      return <WrappedComponent {...this.props} matches={this.state.matches} />
    }
  }
}

export const adFraudNames = type => {
    switch (type) {
        case AdFraudProtectionTypeEnum.LowProtection:
            return 'Low Protection'
        case AdFraudProtectionTypeEnum.ModerateProtection:
            return 'Moderate Protection'
        case AdFraudProtectionTypeEnum.HighProtection:
            return 'High Protection'
        default:
            return ''
    }
}

export const visibilityNames = type => {
    switch (type) {
        case VisibilityTypeEnum._50:
            return '> 50%'
        case VisibilityTypeEnum._60:
            return '> 60%'
        case VisibilityTypeEnum._70:
            return '> 70%'
        default:
            return ''
    }
}

export const lowerize = object => Object.keys(object).reduce((acc, k) => {
    acc[k.toLowerCase()] = object[k]
    return acc
}, {})

type CompoundedComponent = React.FC<SelectProps> & {
    Option: typeof Select.Option;
};

export const SelectWithoutSearch: CompoundedComponent = (props) => <Select {...props} showSearch={false} size="small"/>;
SelectWithoutSearch.Option = Select.Option;

export const formatNames = {
    [CreativeBean.FormatEnum.Pop]: 'Pop-under',
    [CreativeBean.FormatEnum.Push]: 'Push Notification',
    [CreativeBean.FormatEnum.ImageLink]: 'Banner',
    [CreativeBean.FormatEnum.HTML]: 'Banner HTML',
    [CreativeBean.FormatEnum.HTML5]: 'Rich Ads / HTML5',
    [CreativeBean.FormatEnum.SimpleNativeAds]: 'Native',
    [CreativeBean.FormatEnum.NativeAds]: 'Native HTML',
    [CreativeBean.FormatEnum.Audio]: 'Audio',
    [CreativeBean.FormatEnum.Video]: 'Video',
    [CreativeBean.FormatEnum.VideoVast]: 'Video VAST',
    ['Pop']: 'Pop-under',
    ['Push']: 'Push Notification',
    ['Banner']: 'Banner',
    ['Native']: 'Native',
    ['Video']: 'Video',
    undefined: '',
    '': ''
};

export const utmFormat = {
    [CreativeBean.FormatEnum.Pop]: 'pop-under',
    [CreativeBean.FormatEnum.Push]: 'push',
    [CreativeBean.FormatEnum.ImageLink]: 'display',
    [CreativeBean.FormatEnum.HTML]: 'html',
    [CreativeBean.FormatEnum.HTML5]: 'html5',
    [CreativeBean.FormatEnum.NativeAds]: 'native',
    [CreativeBean.FormatEnum.SimpleNativeAds]: 'native',
    [CreativeBean.FormatEnum.Video]: 'video',
    undefined: '',
    '': ''
};

export const transactionTypeLabel = {
    [TransactionBean.TransactionTypeEnum.AGENCYPAY]: 'Agency Pay',
    [TransactionBean.TransactionTypeEnum.ADVERTISERPAY]: 'Advertiser Pay',
    [TransactionBean.TransactionTypeEnum.BIDDINGSPENDS]: 'Spends',
    [TransactionBean.TransactionTypeEnum.ADMINADDING]: 'Admin Adding',
    [TransactionBean.TransactionTypeEnum.ADMINSUBTRACTION]: 'Admin Subtraction',
    [TransactionBean.TransactionTypeEnum.ANTIFRAUDSPENDS]: 'Anti Fraud Spends',
    undefined: '',
    '': ''
};

export const refusedReasonNames = {
    [CreativeBean.RefusedReasonEnum.MisleadingClaims]: 'Misleading claims',
    [CreativeBean.RefusedReasonEnum.ProhibitedContent]: 'Prohibited content',
    [CreativeBean.RefusedReasonEnum.ViolationOfPlatformRules]: 'Violation of platform rules',
    [CreativeBean.RefusedReasonEnum.OffensiveOrShockingImagery]: 'Offensive or shocking imagery',
    [CreativeBean.RefusedReasonEnum.LocalRegulations]: 'Local regulations',
    [CreativeBean.RefusedReasonEnum.TrademarkCopyrightIssues]: 'Trademark/copyright issues',
    [CreativeBean.RefusedReasonEnum.LandingUrlRedirect]: 'Landing URL redirects',
    [CreativeBean.RefusedReasonEnum.LandingUrlUnavailable]: 'Landing URL unavailable',
    undefined: '',
    '': ''
};

export const formatBalance = (dollars?: number) => {
    return dollars?.toLocaleString('us-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    })
    // .replace(/\s/g, '.');

}

export const formatInteger = (value: number) => {
    return value.toLocaleString('ru-RU', {
        useGrouping: true
    })
        .replace(/\s/g, '.');
}
export const formatMoney = (value: number) => {
    return value.toLocaleString('ru-RU', {
        // style: 'currency',
        // currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: true
    })
        // .replace('.', ',')
        .replace(/\s/g, '.');

}
export const formatPercents = (value: number) => {
    return (value / 100).toLocaleString('ru-RU', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).replace(/\s/g, '')
}

export enum Format {
    Cost, Int, Percent
}

export const formatValue = (format: Format, value: number, fractionDigitsForInt?:number) => {
    switch (format) {
        case Format.Cost:
            return '$' + value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        case Format.Int:
            return value.toLocaleString('en-US', {
                minimumFractionDigits: fractionDigitsForInt || 0,
                maximumFractionDigits: fractionDigitsForInt || 0
            })
        case Format.Percent:
            return value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) + '%'
    }
}

export const formatCompact = (format: Format, value?: number, undefinedSymbol?: string): string => {
    if (value === undefined || isNaN(value)) {
        return undefinedSymbol || '-'
    } else if (Math.abs(value) >= 1_000_000_000) {
        return formatValue(format, value / 1_000_000_000, 2) + 'B'
    } else if (Math.abs(value) >= 1_000_000) {
        return formatValue(format, value / 1_000_000, 2) + 'M'
    } else if (Math.abs(value) >= 1_000) {
        return formatValue(format, value / 1_000, 2) + 'K'
    } else {
        return formatValue(format, value)
    }
}

export const showMessage = (messageApi: any, messageType: any, messageText: any) => {
    messageApi.open({
        type: messageType,
        content: messageText,
        duration: 5,
        style: {
            marginTop: '-5px',
            marginRight: '20em'
        }
    });
};

export const getCookie = (name: string) => {
    const matches = document.cookie.match(new RegExp(
        `(?:^|; )${name.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1')}=([^;]*)`
    ));
    return matches ? decodeURIComponent(matches[1]) : null;
};

export function convertError(err: any) {
    const resp = err as Response
    let parsedError: string

    if (resp && resp.status === 401) {
        parsedError = SESSION_IS_EXPIRED
    } else if (resp && resp.status === 403) {
        parsedError = ACCESS_IS_DENIED
    } else if (resp && typeof resp.json === 'function') {
        return resp
            .json()
            .catch(
                () => 'Can\'t connect to the server'
            )
            .then(
                (json) => {
                    if (json.errors instanceof Array && json.errors.length > 0) {
                        let message = ''
                        json.errors.forEach((error) => {
                            const errMessage = error && (error['description'] || error['error'])
                            if (errMessage) {
                                if (message !== '') {
                                    message += ', '
                                }
                                message += errMessage
                            } else {
                                message += JSON.stringify(error)
                            }
                        })
                        return message
                    }

                    if (json.error) {
                        return json.error
                    }

                    return JSON.stringify(json)
                }
            )
    } else if (resp && (resp as any).response && (resp as any).response.data) {
        const {data} = (resp as any).response
        parsedError = data.error || JSON.stringify(data)
    } else if (err && err.error) {
        parsedError = err.error
    } else if (err && err.message) {
        if (err.message.includes('Failed to fetch')) {
            parsedError = 'Can\'t connect to the server'
        } else {
            parsedError = err.message
        }
        console.error(err)
    } else if (typeof err === "string" || err instanceof String) {
        parsedError = err.toString()
    } else {
        console.error(err)
        parsedError = JSON.stringify(err)
    }

    return Promise.resolve(parsedError)
}

export function showError(appContext: AppContextType, err: any) {
    convertError(err).then(errorText => {
        appContext.setError(errorText)
    })
}

export function apiCall<T>(
    setLoading: (value: boolean) => void,
    appContext: AppContextType,
    apiFunction: () => Promise<T>,
    callback: (data: T) => void,
    errorText?: string
) {
    setLoading(true)
    apiFunction().then(callback).catch(e => showError(appContext, errorText || e)).finally(() => setLoading(false))
}

export interface FormProps {
    mode: FormMode
    agency: boolean
}

export enum FormMode {
    create = 'create',
    edit = 'edit',
    copy = 'copy'
}

export const formatDollars = (dollars: number) => {
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
    useGrouping: true,
  })
}

export const camelCaseToWords = (str: string) => (str ? str.replace(/([a-z])([A-Z])/g, '$1 $2') : '')

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
